import { useEffect, useState } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { ClickTracking } from '@/components/ClickTracking';
import { ChevronDownIcon, GlobeIcon, XIcon } from '@/icons';
import { useIsMobileHeaderViewport } from '@/layout/Header/hooks/useIsMobileHeaderViewport';
import { useLockBodyScroll } from '@/layout/Header/hooks/useLockBodyScroll';
import { useUser } from '@/lib/auth';
import { useLanguageChange } from '@/shared/components/LanguageSelector/hooks/useLanguageChange';
import { useLanguageOptions } from '@/shared/components/LanguageSelector/hooks/useLanguageOptions';
import { cn } from '@/utils/tailwind.util';
import { type GnbButtonType, type GnbSubButtonType } from '../GnbButtonType';
import { MyQuizButton } from '../PageNavigatorButton';
import { LogInButton } from '../UserManagementButton';
import { TriggerContent } from './TriggerContent';
interface Props {
  logo?: React.ReactNode;
  buttons: GnbButtonType[];
  bodyBottomAccessory?: React.ReactNode;
  footerBottomAccessory?: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
}
export const MobileDrawer = ({
  buttons,
  bodyBottomAccessory,
  footerBottomAccessory,
  onOpenChange
}: Props) => {
  const {
    user
  } = useUser();
  const isMobileHeaderViewport = useIsMobileHeaderViewport();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openLabels, setOpenLabels] = useState<string[]>([]);
  const toggleOpen = () => {
    setIsOpen(prev => {
      const nextIsOpen = !prev;
      onOpenChange?.(nextIsOpen);
      return nextIsOpen;
    });
  };
  const toggleSubMenu = (label: string) => {
    setOpenLabels(prev => prev.includes(label) ? prev.filter(openLabel => openLabel !== label) : [...prev, label]);
  };
  const closeDrawer = () => {
    setIsOpen(false);
    setOpenLabels([]);
    onOpenChange?.(false);
  };
  useEffect(() => {
    if (isMobileHeaderViewport || !isOpen) return;
    setIsOpen(false);
    setOpenLabels([]);
    onOpenChange?.(false);
  }, [isMobileHeaderViewport, isOpen, onOpenChange]);
  useLockBodyScroll(isOpen);
  const visibleButtons = buttons.filter(button => !button.isHidden);
  const defaultFooter = user ? <div className="flex flex-col gap-sm">
      <MyQuizButton.Drawer onClick={toggleOpen} />
    </div> : <LogInButton type="drawer" className="h-[50px]" />;
  return <>
      {isOpen ? <button type="button" aria-label="Close menu" aria-expanded={isOpen} aria-haspopup="menu" className="flex size-[40px] items-center justify-center rounded-sm tablet-sm:hidden" onClick={toggleOpen}>
          <XIcon size="md" fill={ICON_COLOR.neutral} />
        </button> : <TriggerContent onClick={toggleOpen} aria-expanded={isOpen} aria-haspopup="menu" />}
      {isOpen && <>
          <button type="button" aria-label="Close menu" className="fixed inset-x-0 bottom-0 top-[50px] z-[100000000] w-screen max-w-[100vw] overscroll-none bg-dimmed tablet-sm:hidden" onClick={closeDrawer} onWheel={event => event.preventDefault()} onTouchMove={event => event.preventDefault()} />
          <div className="fixed inset-x-0 top-[50px] z-[100000001] max-h-[calc(100dvh-50px)] w-screen max-w-[100vw] overflow-x-hidden overscroll-none bg-default tablet-sm:hidden">
            <div className="flex max-h-[calc(100dvh-50px)] w-full min-w-0 max-w-full flex-col overflow-x-hidden overscroll-none">
              <div className="min-h-0 w-full min-w-0 max-w-full overflow-y-auto overflow-x-hidden overscroll-contain pt-xs">
                <div className="flex w-full min-w-0 max-w-full flex-col gap-xs px-md">
                  {visibleButtons.map(button => <MobileMenuItem key={button.label} button={button} isOpen={openLabels.includes(button.label)} onClose={closeDrawer} onToggle={() => toggleSubMenu(button.label)} />)}
                </div>
                {Boolean(bodyBottomAccessory) && <div className="mt-sm w-full min-w-0 max-w-full">
                    {bodyBottomAccessory}
                  </div>}
              </div>
              <div className="w-full min-w-0 max-w-full shrink-0 overflow-x-hidden border-t border-alternative px-md pb-md pt-[12px]">
                <div className="flex w-full min-w-0 max-w-full flex-col gap-xs">
                  {footerBottomAccessory ?? defaultFooter}
                </div>
              </div>
            </div>
          </div>
        </>}
    </>;
};
MobileDrawer.TriggerContent = TriggerContent;
export const MobileDrawerLanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const languageOptions = useLanguageOptions();
  const {
    currentLocale,
    handleLanguageChange
  } = useLanguageChange({
    location: 'gnb_mobile'
  });
  return <div className="border-t border-alternative" data-sentry-component="MobileDrawerLanguageSelector" data-sentry-source-file="MobileDrawer.tsx">
      <div className="px-md py-[12px]">
        <button type="button" className="flex h-[48px] w-full items-center rounded-sm text-body-lg-semibold text-neutral hover:bg-opacity-black-1" onClick={() => setIsOpen(prev => !prev)}>
          <span className="flex size-[40px] shrink-0 items-center justify-center">
            <GlobeIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="GlobeIcon" data-sentry-source-file="MobileDrawer.tsx" />
          </span>
          <span className="min-w-0 flex-1 truncate text-left uppercase">
            {currentLocale}
          </span>
          <span className="flex size-[40px] shrink-0 items-center justify-center">
            <ChevronDownIcon size="sm" fill={ICON_COLOR.neutral} className={isOpen ? 'rotate-180 transition-transform' : 'transition-transform'} data-sentry-element="ChevronDownIcon" data-sentry-source-file="MobileDrawer.tsx" />
          </span>
        </button>
        {isOpen && <div className="pb-xs">
            {languageOptions.map(option => {
          const isSelected = currentLocale === option.value;
          return <button key={option.value} type="button" className={isSelected ? 'flex h-[40px] w-full items-center rounded-sm px-[40px] text-left text-body-md-semibold text-strong hover:bg-opacity-black-1' : 'flex h-[40px] w-full items-center rounded-sm px-[40px] text-left text-body-md-semibold text-neutral hover:bg-opacity-black-1'} onClick={() => {
            void handleLanguageChange(option.value);
          }}>
                  <span className="min-w-0 flex-1 truncate">
                    {option.label}
                  </span>
                </button>;
        })}
          </div>}
      </div>
    </div>;
};
const MobileMenuItem = ({
  button,
  isOpen,
  onToggle,
  onClose
}: {
  button: GnbButtonType;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) => {
  const subButtons = button.subButtons?.filter(subButton => !subButton.isHidden && subButton.clickEventProperties?.recent !== true);
  const hasSubButtons = Boolean(subButtons?.length);
  const mainButtonContent = <>
      <span className="min-w-0 truncate">{button.label}</span>
      {hasSubButtons && <span className="flex size-[40px] shrink-0 items-center justify-center">
          <ChevronDownIcon size="sm" className={cn('transition-transform duration-100', isOpen && 'rotate-180')} />
        </span>}
    </>;
  return <div className="flex w-full min-w-0 max-w-full flex-col gap-xs overflow-hidden" data-sentry-component="MobileMenuItem" data-sentry-source-file="MobileDrawer.tsx">
      <ClickTracking eventName={button.clickEventname} eventProperties={button.clickEventProperties} data-sentry-element="ClickTracking" data-sentry-source-file="MobileDrawer.tsx">
        {hasSubButtons ? <button type="button" className="flex h-[48px] w-full min-w-0 max-w-full items-center justify-between overflow-hidden rounded-xs pl-[8px] pr-none text-left text-body-lg-semibold text-default hover:bg-opacity-black-1" onClick={onToggle}>
            {mainButtonContent}
          </button> : button.href ? <Link href={button.href} target={button.target} prefetch={false} className="flex h-[48px] w-full min-w-0 max-w-full items-center overflow-hidden rounded-xs pl-[8px] pr-md text-body-lg-semibold text-default hover:bg-opacity-black-1" onClick={() => {
        button.onClick?.();
        onClose();
      }}>
            {button.label}
          </Link> : <button type="button" className="flex h-[48px] w-full min-w-0 max-w-full items-center overflow-hidden rounded-xs pl-[8px] pr-md text-body-lg-semibold text-default hover:bg-opacity-black-1" onClick={() => {
        button.onClick?.();
        onClose();
      }}>
            {button.label}
          </button>}
      </ClickTracking>
      {hasSubButtons && isOpen && <div className="flex w-full min-w-0 max-w-full flex-col gap-xs overflow-hidden">
          {subButtons?.map((subButton, index) => <MobileSubMenuItem key={`${subButton.label}-${index}`} subButton={subButton} onClose={onClose} />)}
        </div>}
    </div>;
};
const MobileSubMenuItem = ({
  subButton,
  onClose
}: {
  subButton: GnbSubButtonType;
  onClose: () => void;
}) => <ClickTracking eventName={subButton.clickEventname} eventProperties={subButton.clickEventProperties} data-sentry-element="ClickTracking" data-sentry-component="MobileSubMenuItem" data-sentry-source-file="MobileDrawer.tsx">
    <Link href={subButton.href} target={subButton.target} prefetch={false} className="flex h-[48px] w-full min-w-0 max-w-full items-center overflow-hidden rounded-xs pl-xl pr-md text-body-lg-semibold text-default hover:bg-opacity-black-1" onClick={() => {
    subButton.onClick?.();
    onClose();
  }} data-sentry-element="Link" data-sentry-source-file="MobileDrawer.tsx">
      {subButton.icon && <span className="mr-xs flex size-[28px] shrink-0 items-center justify-center">
          {subButton.icon}
        </span>}
      <span className="min-w-0 truncate">{subButton.label}</span>
    </Link>
  </ClickTracking>;